import type { OutreachBatch } from '@secretario/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from './api/http.js';
import {
  listOutreach,
  OUTREACH_KEY,
  openNextOutreach,
  outreachDeviceGrants,
} from './api/outreach.js';
import { nativeBridge, nativeNotices, type OutreachDeviceSummary } from './secretarioNative.js';
import { isNativeShell } from './platform.js';
import { useProfile } from './useProfile.js';
import { signWhatsApp } from './whatsapp.js';

/**
 * O que a página do envio, a cápsula e o cartão da mesa dividem (estudo do envio minimizado):
 * a lista que se atualiza sozinha enquanto há envio andando, a contagem dos 60 s pela hora do
 * servidor e o jeito de abrir o próximo WhatsApp sem o navegador bloquear a janela.
 */
export const isRunning = (batch: OutreachBatch) =>
  batch.state === 'enviando' || batch.state === 'pausado';

/** Os envios da pessoa; a cada 3 s enquanto algum anda, a cada minuto quando nada anda. */
export function useOutreachList(enabled = true) {
  return useQuery({
    queryKey: OUTREACH_KEY,
    queryFn: listOutreach,
    enabled,
    staleTime: 2_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => ((query.state.data ?? []).some(isRunning) ? 3_000 : 60_000),
    retry: false,
  });
}

/** Segundos até `iso` (a hora vem do servidor), redesenhado a cada meio segundo até zerar. */
export function useSecondsUntil(iso: string | null | undefined): number {
  const target = iso ? new Date(iso).getTime() : 0;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!target || target <= Date.now()) return;
    const timer = window.setInterval(() => {
      setTick((tick) => tick + 1);
      if (Date.now() >= target) window.clearInterval(timer);
    }, 500);
    return () => window.clearInterval(timer);
  }, [target]);
  return target ? Math.max(0, Math.ceil((target - Date.now()) / 1000)) : 0;
}

export const clock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** O envio que a cápsula mostra: o que tem WhatsApp pronto ou aberto primeiro, depois o mais novo. */
export function focusOf(batches: OutreachBatch[]): { batch: OutreachBatch; more: number } | null {
  const running = batches.filter(isRunning);
  if (running.length === 0) return null;
  const score = (batch: OutreachBatch) =>
    batch.state === 'pausado' ? 0 : batch.open || batch.next ? 2 : 1;
  const sorted = [...running].sort((a, b) => score(b) - score(a));
  return { batch: sorted[0]!, more: running.length - 1 };
}

/** O texto do erro do servidor quando ele explica (espera, teto, pausado); senão o genérico. */
export function outreachError(error: unknown): string {
  if (error instanceof ApiError && error.code.startsWith('outreach_')) return error.message;
  return 'Não consegui agora. Tente de novo em instantes.';
}

/**
 * Abre o próximo WhatsApp. No navegador a janela nasce no toque (senão é bloqueada) e recebe o
 * endereço quando o servidor libera; no app, o endereço vai para o WhatsApp instalado.
 */
/** O texto do wa.me com a assinatura Ⓢ no fim, como as outras mensagens que o Secretário prepara. */
export function signedLink(link: string, signed: boolean): string {
  const at = link.indexOf('?text=');
  if (at < 0) return link;
  const text = decodeURIComponent(link.slice(at + 6));
  return `${link.slice(0, at)}?text=${encodeURIComponent(signWhatsApp(text, signed))}`;
}

export function useOpenNext() {
  const queryClient = useQueryClient();
  const signed = useProfile().data?.preferences.whatsappSignature !== false;
  return async (batchId: string, previous?: 'enviado' | 'pulado') => {
    const native = isNativeShell();
    const early = native ? null : window.open('about:blank', '_blank');
    try {
      const result = await openNextOutreach(batchId, previous);
      const link = signedLink(result.link, signed);
      if (native) window.open(link, '_system');
      else if (early) early.location.href = link;
      else window.open(link, '_blank', 'noopener');
      return result;
    } catch (error) {
      early?.close();
      throw error;
    } finally {
      void queryClient.invalidateQueries({ queryKey: OUTREACH_KEY });
    }
  };
}

/** O resumo do aviso fixo (igual ao `deviceSummary` do servidor): só o primeiro nome da vez. */
export function deviceSummaryOf(batch: OutreachBatch): OutreachDeviceSummary {
  return {
    id: batch.id,
    title: batch.title,
    state: batch.state,
    sent: batch.counts.appSent + batch.counts.whatsappSent,
    reach: batch.counts.app + batch.counts.whatsapp,
    waSent: batch.counts.whatsappSent,
    waTotal: batch.counts.whatsapp,
    next: batch.next ? firstName(batch.next.name) : null,
    readyAt: batch.next ? new Date(batch.next.readyAt).getTime() : 0,
    hold: batch.next?.hold ?? 'nenhum',
    open: batch.open ? firstName(batch.open.name) : null,
  };
}

/** Vales por envio, pedidos uma vez a cada 24 h (valem 36 h no servidor). */
const grantCache = new Map<string, { at: number; grants: Record<string, string> }>();

/**
 * No Android, o aviso fixo acompanha o envio em foco: redesenha a cada mudança (andamento, vez,
 * pausa) e some quando nada anda. Os botões dele resolvem sem o app aberto.
 */
export function useNativeOutreachNotice(batch: OutreachBatch | null) {
  const summary = batch ? deviceSummaryOf(batch) : null;
  const key = summary ? JSON.stringify(summary) : '';
  const shown = useRef(false);
  useEffect(() => {
    if (!nativeNotices()) return;
    if (!summary) {
      if (shown.current) void nativeBridge.outreachClear();
      shown.current = false;
      return;
    }
    let cancelled = false;
    void (async () => {
      const cached = grantCache.get(summary.id);
      let grants = cached && Date.now() - cached.at < 24 * 3_600_000 ? cached.grants : null;
      if (!grants) {
        grants = await outreachDeviceGrants(summary.id).catch(() => null);
        if (grants) grantCache.set(summary.id, { at: Date.now(), grants });
      }
      if (cancelled) return;
      await nativeBridge.outreachShow(summary, grants ?? undefined);
      shown.current = true;
    })();
    return () => {
      cancelled = true;
    };
    // O resumo inteiro vai na chave: qualquer mudança redesenha o aviso.
  }, [key]);
}
